/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, July 25th 2022, 5:20:33 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { AlertSnackBar, useToggleState, ToggleState, ActionDialog } from "ui-widgets";
import { useServiceBuilderContext } from "service-builder/providers";
import { StatusUIProperties } from "./ProcessDefinitionUIProperties";

type ProcessDefinitionStatusChangeProps = Pick<ToggleState, "handleClose" | "open">;

export const ProcessDefinitionStatusChange = ({ handleClose, open }: ProcessDefinitionStatusChangeProps) => {
  const {
    dialogText,
    dialogStyles,
    successDialogOpenState,
    successMessage,
    isUpdatingStatus,
    closeSuccessDialog,
    onConfirmation,
  } = useProcessDefinitionStatusChange({ handleClose, open });

  return (
    <>
      <AlertSnackBar
        open={successDialogOpenState}
        message={<>{successMessage}</>}
        vertical="top"
        horizontal="center"
        handleClose={closeSuccessDialog}
        autoClose
        severity="success"
        sx={dialogStyles}
      />
      <ActionDialog
        severity="warning"
        title="Confirmation"
        open={open}
        onClose={handleClose}
        confirmationAction={onConfirmation}
        dialogMessage={<>{dialogText} Are you sure about this?</>}
        isPeformingAction={isUpdatingStatus}
      />
    </>
  );
};

const useProcessDefinitionStatusChange = ({ handleClose, open }: ProcessDefinitionStatusChangeProps) => {
  const {
    patchCurrentProcessDefinition,
    processDefinitionContext: {
      processDefinitionPatchState,
      currentProcessDefinition,
      newStatusForCurrentProcessDefinition,
    },
  } = useServiceBuilderContext();

  let mode: string;
  switch (currentProcessDefinition?.status) {
    case "DEPLOYED":
      mode = "LIVE";
      break;
    case "PRE_DEPLOYMENT_TESTING":
      mode = "TEST";
      break;
    default:
      mode = "DEVELOPMENT";
  }

  const isAvailableToPublic = !currentProcessDefinition?.extra_configurations?.is_initiated_by_worker;

  let dialogText: React.ReactNode;

  switch (newStatusForCurrentProcessDefinition) {
    case "DEVELOPMENT":
      dialogText = (
        <>
          You are about to switch to development mode. This will make your service inaccessible to everyone while you
          work on configuring it.
        </>
      );
      break;
    case "PRE_DEPLOYMENT_TESTING":
      dialogText = (
        <>
          You are about to switch to test mode. This will make your service accessible only to organization admins for
          testing.
        </>
      );
      break;
    case "DEPLOYED":
      dialogText = (
        <>
          You are about to deploy your service. This will make it available to{" "}
          {isAvailableToPublic && "public users as well as "}workers in your organization. All test data will be removed
          from storage.
        </>
      );
      break;
    default:
      dialogText = null;
  }

  const onConfirmation = () => {
    patchCurrentProcessDefinition({ status: newStatusForCurrentProcessDefinition });
  };

  const {
    handleClose: closeSuccessDialog,
    handleOpen: openSuccessDialog,
    open: successDialogOpenState,
  } = useToggleState();

  React.useEffect(() => {
    if (processDefinitionPatchState.isSuccessfulProcessDefinitionPatch) {
      openSuccessDialog();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProcessDefinition?.status]);

  return {
    currentProcessDefinition,
    dialogText,
    dialogStyles: { backgroundColor: StatusUIProperties?.[currentProcessDefinition?.status]?.color, color: "white" },
    successDialogOpenState,
    successMessage: `Successfully updated. ${currentProcessDefinition?.title} is now in ${mode} mode.`,
    isUpdatingStatus: processDefinitionPatchState?.isPatchingProcessDefinition,
    closeSuccessDialog,
    onConfirmation,
  };
};
